import React, { Controller } from "react-hook-form";
import { Col, Row, Label } from "reactstrap";
import { Stack } from "react-bootstrap";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { AntInput } from "../../../../CommonElements/AntInput";
import { AntSelect } from "../../../../CommonElements/AntSelect";
import { useTranslation } from "react-i18next";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

/**
 * Common filter component for Campaign and Group reports
 * @param {Object} props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - React Hook Form errors object
 * @param {'campaign'|'group'} props.type - Type of report (determines which fields to show)
 */
export const ReportFilters = ({
  control,
  errors,
  type = "campaign",
  isPdf = false,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();

  const formatOptions = [
    { value: "json", label: t("VIEW_REPORTS") },
    { value: "pdf", label: t("EXPORT_PDF") },
    { value: "excel", label: t("EXPORT_EXCEL") },
  ];

  const statusOptions = [
    { value: "scheduled", label: t("SCHEDULED") || "Scheduled" },
    {
      value: "arrived_airport",
      label: t("ARRIVED_AIRPORT") || "Arrived at Airport",
    },
  ];

  const orientationOptions = [
    { value: "portrait", label: t("PORTRAIT") },
    { value: "landscape", label: t("LANDSCAPE") },
  ];

  return (
    <Row>
      {/* Format Field - Required */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="format">
            {t("REPORT_FORMAT") || "Format"}
          </Label>
          <Controller
            name="format"
            control={control}
            rules={{
              required: t("FIELD_REQUIRED") || "This field is required",
            }}
            render={({ field }) => (
              <AntSelect
                allowClear
                {...field}
                id="format"
                placeholder={t("SELECT_FORMAT") || "Select format"}
                status={errors.format ? "error" : undefined}
                options={formatOptions}
              />
            )}
          />
          {errors.format && (
            <div className="text-danger mt-1">{errors.format?.message}</div>
          )}
        </Stack>
      </Col>
      {isPdf && (
        <Col md={6}>
          <Stack gap={1} className="mb-3">
            <Label className="fw-bold" for="orientation">
              {t("ORIENTATION") || "Orientation"}
            </Label>
            <Controller
              name="orientation"
              control={control}
              render={({ field }) => (
                <AntSelect
                  {...field}
                  id="orientation"
                  placeholder={t("SELECT_ORIENTATION") || "Select orientation"}
                  options={orientationOptions}
                />
              )}
            />
            {errors.format && (
              <div className="text-danger mt-1">{errors.format?.message}</div>
            )}
          </Stack>
        </Col>
      )}

      {/* Flight Date From */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="flight_date_from">
            {t("FLIGHT_DATE_FROM") || "Flight Date From"}
          </Label>
          <Controller
            name="flight_date_from"
            control={control}
            render={({ field }) => (
              <DatePicker
                size="large"
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => {
                  field.onChange(date ? date.format("YYYY-MM-DD") : null);
                }}
                id="flight_date_from"
                className="w-100"
                placeholder={t("FLIGHT_DATE_FROM_PLACEHOLDER") || "Select date"}
                status={errors.flight_date_from ? "error" : undefined}
                format="YYYY-MM-DD"
              />
            )}
          />
          {errors.flight_date_from && (
            <div className="text-danger mt-1">
              {errors.flight_date_from?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Flight Date To */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="flight_date_to">
            {t("FLIGHT_DATE_TO") || "Flight Date To"}
          </Label>
          <Controller
            name="flight_date_to"
            control={control}
            render={({ field }) => (
              <DatePicker
                size="large"
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => {
                  field.onChange(date ? date.format("YYYY-MM-DD") : null);
                }}
                id="flight_date_to"
                className="w-100"
                placeholder={t("FLIGHT_DATE_TO_PLACEHOLDER") || "Select date"}
                status={errors.flight_date_to ? "error" : undefined}
                format="YYYY-MM-DD"
              />
            )}
          />
          {errors.flight_date_to && (
            <div className="text-danger mt-1">
              {errors.flight_date_to?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Campaign Number (for campaigns) or Group Number (for groups) */}
      {type === "campaign" ? (
        <Col md={6}>
          <Stack gap={1} className="mb-3">
            <Label className="fw-bold" for="campaign_number">
              {t("CAMPAIGN_NUMBER") || "Campaign Number"}
            </Label>
            <Controller
              name="campaign_number"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  id="campaign_number"
                  placeholder={
                    t("CAMPAIGN_NUMBER_PLACEHOLDER") || "Enter campaign number"
                  }
                  status={errors.campaign_number ? "error" : undefined}
                />
              )}
            />
            {errors.campaign_number && (
              <div className="text-danger mt-1">
                {errors.campaign_number?.message}
              </div>
            )}
          </Stack>
        </Col>
      ) : (
        <Col md={6}>
          <Stack gap={1} className="mb-3">
            <Label className="fw-bold" for="group_number">
              {t("GROUP_NUMBER") || "Group Number"}
            </Label>
            <Controller
              name="group_number"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  id="group_number"
                  placeholder={
                    t("GROUP_NUMBER_PLACEHOLDER") || "Enter group number"
                  }
                  status={errors.group_number ? "error" : undefined}
                />
              )}
            />
            {errors.group_number && (
              <div className="text-danger mt-1">
                {errors.group_number?.message}
              </div>
            )}
          </Stack>
        </Col>
      )}

      {/* Flight Number */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="flight_number">
            {t("FLIGHT_NUMBER") || "Flight Number"}
          </Label>
          <Controller
            name="flight_number"
            control={control}
            render={({ field }) => (
              <AntInput
                {...field}
                id="flight_number"
                placeholder={
                  t("FLIGHT_NUMBER_PLACEHOLDER") || "Enter flight number"
                }
                status={errors.flight_number ? "error" : undefined}
              />
            )}
          />
          {errors.flight_number && (
            <div className="text-danger mt-1">
              {errors.flight_number?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Entry Port */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="entry_port_id">
            {t("ENTRY_PORT") || "Entry Port"}
          </Label>
          <Controller
            name="entry_port_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                {...field}
                allowClear
                showSearch
                id="entry_port_id"
                placeholder={t("SELECT_ENTRY_PORT") || "Select entry port"}
                status={errors.entry_port_id ? "error" : undefined}
                optionKey="ports"
                options={(port) => ({
                  value: port.id,
                  label: (
                    <LabelValue.Value
                      value={
                        getLocalizedText(port?.name_ar, port?.name_en) ||
                        port?.name
                      }
                    />
                  ),
                })}
              />
            )}
          />
          {errors.entry_port_id && (
            <div className="text-danger mt-1">
              {errors.entry_port_id?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Direction */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="direction_id">
            {t("DIRECTION") || "Direction"}
          </Label>
          <Controller
            name="direction_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                allowClear
                showSearch
                {...field}
                disabled
                id="direction_id"
                placeholder={t("SELECT_DIRECTION") || "Select direction"}
                status={errors.direction_id ? "error" : undefined}
                optionKey="directions"
                options={(direction) => ({
                  value: direction.id,
                  label: <LabelValue.Value value={direction.name} />,
                })}
              />
            )}
          />
          {errors.direction_id && (
            <div className="text-danger mt-1">
              {errors.direction_id?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Coming From */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="coming_from_id">
            {t("COMING_FROM") || "Coming From"}
          </Label>
          <Controller
            name="coming_from_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                allowClear
                showSearch
                {...field}
                id="coming_from_id"
                placeholder={t("SELECT_COMING_FROM") || "Select coming from"}
                status={errors.coming_from_id ? "error" : undefined}
                optionKey="directions"
                options={(direction) => ({
                  value: direction.id,
                  label: <LabelValue.Value value={direction.name} />,
                })}
              />
            )}
          />
          {errors.coming_from_id && (
            <div className="text-danger mt-1">
              {errors.coming_from_id?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Airline */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="airline_id">
            {t("AIRLINE") || "Airline"}
          </Label>
          <Controller
            name="airline_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                allowClear
                {...field}
                id="airline_id"
                showSearch
                placeholder={t("SELECT_AIRLINE") || "Select airline"}
                status={errors.airline_id ? "error" : undefined}
                optionKey="airlines"
                options={(airline) => ({
                  value: airline.id,
                  label: (
                    <LabelValue.Value
                      value={
                        getLocalizedText(airline?.name_ar, airline?.name_en) ||
                        airline?.name
                      }
                    />
                  ),
                })}
              />
            )}
          />
          {errors.airline_id && (
            <div className="text-danger mt-1">{errors.airline_id?.message}</div>
          )}
        </Stack>
      </Col>

      {/* Capacity */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="capacity">
            {t("PILGRIMS_COUNT") || "Capacity"}
          </Label>
          <Controller
            name="capacity"
            control={control}
            render={({ field }) => (
              <AntInput
                {...field}
                type="number"
                id="capacity"
                placeholder={t("CAPACITY_PLACEHOLDER") || "Enter capacity"}
                status={errors.capacity ? "error" : undefined}
              />
            )}
          />
          {errors.capacity && (
            <div className="text-danger mt-1">{errors.capacity?.message}</div>
          )}
        </Stack>
      </Col>

      {/* Service Center */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="service_center_id">
            {t("SERVICE_CENTER") || "Service Center"}
          </Label>
          <Controller
            name="service_center_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                allowClear
                {...field}
                showSearch
                status={errors.service_center_id ? "error" : undefined}
                placeholder={
                  t("SELECT_SERVICE_CENTER") || "Select service center"
                }
                id="service_center_id"
                optionKey="service_centers"
                options={(service_center) => ({
                  value: service_center.id,
                  label: <LabelValue.Value value={service_center.name} />,
                })}
              />
            )}
          />
          {errors.service_center_id && (
            <div className="text-danger mt-1">
              {errors.service_center_id?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Buses Count */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="buses_count">
            {t("BUS_CARDS_COUNT") || "Bus Cards Count"}
          </Label>
          <Controller
            name="buses_count"
            control={control}
            render={({ field }) => (
              <AntInput
                {...field}
                type="number"
                id="buses_count"
                placeholder={
                  t("BUSES_COUNT_PLACEHOLDER") || "Enter buses count"
                }
                status={errors.buses_count ? "error" : undefined}
              />
            )}
          />
          {errors.buses_count && (
            <div className="text-danger mt-1">
              {errors.buses_count?.message}
            </div>
          )}
        </Stack>
      </Col>

      {/* Status */}
      <Col md={6}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="status">
            {t("STATUS") || "Status"}
          </Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <AntSelect
                allowClear
                {...field}
                id="status"
                placeholder={t("SELECT_STATUS") || "Select status"}
                status={errors.status ? "error" : undefined}
                options={statusOptions}
              />
            )}
          />
          {errors.status && (
            <div className="text-danger mt-1">{errors.status?.message}</div>
          )}
        </Stack>
      </Col>
    </Row>
  );
};
